import ajax from '../../../../components/ajax';
import {
  DuplicateDialogProps,
  useDuplicateWorkout,
} from '@client/new/features/workout-duplicate';
import { useGetDraftWorkoutsQuery } from '../../../../../generated/graphql';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { toLiteWorkout } from '@client/new/pages/collection/workouts/workouts.hooks';
import { LiteWorkout } from '@client/new/pages/collection/workouts/workouts.domain';
import {
  WorkoutsSearchVM,
  buildWorkoutsListTable,
  filterWorkoutsByTitle,
  toProgramNameById,
} from './workoutTableExtras';
import {
  WorkoutsPagination,
  useWorkoutsPageData,
  useWorkoutsSearch,
} from './workoutsList.hooks';

type DraftWorkoutsVM = {
  isLoading: boolean;
  creator: { id: number };
  isEmpty: boolean;
  table: SmartTableProps<number>;
  search: WorkoutsSearchVM;
  pagination: WorkoutsPagination;
  duplicateDialog: DuplicateDialogProps | null;
};

export const useDraftWorkouts = (
  influencerIdParam?: string,
): DraftWorkoutsVM => {
  const { influencerId, isLoading, creator, workouts, refetch, pagination } =
    useWorkoutsPageData({
      influencerIdParam,
      useQuery: useGetDraftWorkoutsQuery,
      selectWorkouts: (data) => data?.creator?.draftWorkouts ?? [],
      fetchPolicy: 'network-only',
    });
  const { searchText, search } = useWorkoutsSearch();

  const liteWorkouts: LiteWorkout[] = workouts.map((w) =>
    toLiteWorkout(w, 'draft'),
  );

  const { duplicate, dialog: duplicateDialog } = useDuplicateWorkout(
    influencerId,
    (workoutId, targetProgramId) => {
      ajax.cloneWorkout(workoutId, targetProgramId).then(refetch);
    },
  );
  const duplicateById = (workoutId: number) => {
    const workout = liteWorkouts.find((w) => w.workoutId === workoutId);
    if (workout) duplicate(workout);
  };

  const remove = (workoutId: number) => {
    if (confirm('Are you sure you want to *delete* this Workout?'))
      ajax.deleteWorkout(workoutId).then(refetch);
  };

  return {
    isLoading,
    creator,
    isEmpty: workouts.length === 0,
    table: {
      type: 'standard',
      value: buildWorkoutsListTable({
        kind: 'draft',
        influencerId,
        workouts: filterWorkoutsByTitle(liteWorkouts, searchText),
        programNameById: toProgramNameById(workouts),
        actions: { onDuplicate: duplicateById, onDelete: remove },
      }),
    },
    search,
    pagination,
    duplicateDialog,
  };
};
